export const LEDGER_ENTRY_KIND = [
    "talk_charge",
    "credit_purchase",
    "refund",
    "adjustment",
    "review_charge",
    "trial_grant",
    "number_charge",
];
/**
 * plan-014 D4 (migration 0096, design §27.3.21): the kinds the ledger gained for every plan-014 need at once, so
 * E5, F3c and F3d never touch the single ledger writer. Kept apart from {@link LEDGER_ENTRY_KIND} — what the
 * console's Transactions screen labels today, one label per kind — until D6 gives each of these its words;
 * {@link POSTED_LEDGER_ENTRY_KIND} is the database's whole CHECK.
 *
 *   transfer_out / transfer_in   a linked transfer between two balances of one payer (`ledger_transfer`)
 *   operator_credit              "Credit from WeTalk" granted by an operator (reason required)
 *   bank_payment                 a bank transfer an operator allocated to a balance (reason required)
 *   subscription_charge, add_on_charge, statement_payment, credit_note, dispute_debit, dispute_reversal
 */
export const PLAN_014_LEDGER_ENTRY_KIND = [
    "transfer_out",
    "transfer_in",
    "operator_credit",
    "bank_payment",
    "subscription_charge",
    "add_on_charge",
    "credit_note",
    "statement_payment",
    "dispute_debit",
    "dispute_reversal",
];
/**
 * plan-020 DOM (design §32.4.1, §32.8.1; ADR 0070, proposed; glossary §6.35 "Subscription payment"; added 2026-10-16):
 * the positive leg of a plan fee's net-zero pair with `subscription_charge` on the same balance, posted only by
 * `postSubscriptionFeePair` (`packages/db/src/tenant/ledger.ts`, FEE). Migration 0159 (DB-SUB) restates the ledger
 * CHECK with it, a sign CHECK per kind and a deferred trigger that refuses an unpaired leg. Kept apart from
 * {@link LEDGER_ENTRY_KIND} (the console's labelled kinds) like the plan-014 kinds; it is in
 * {@link POSTED_LEDGER_ENTRY_KIND}, the database's whole CHECK.
 */
export const SUBSCRIPTION_FEE_LEDGER_ENTRY_KIND = ["subscription_payment"];
/**
 * plan-029 PRICE-SEAM (design §40.25.1; ADR 0082, proposed; the owner's AP-0 answer D-9, 2026-10-27; added 2026-10-29):
 * one confirmation code sent, charged at the price pinned at the send (`confirmation_code_charge`), a debit only
 * (migration 0204's `ledger_entry_confirmation_code_charge_shape`), posted once per charge by the worker's
 * `confirmation_code.charged` handler through `postConfirmationCodeCharge`. Kept apart from {@link LEDGER_ENTRY_KIND}
 * (the console's labelled kinds) like the plan-014 kinds — STMT-CODE gives it its words; it is in
 * {@link POSTED_LEDGER_ENTRY_KIND}, the database's whole CHECK.
 */
export const CONFIRMATION_CODE_LEDGER_ENTRY_KIND = ["confirmation_code_charge"];
export const POSTED_LEDGER_ENTRY_KIND = [
    ...LEDGER_ENTRY_KIND,
    ...PLAN_014_LEDGER_ENTRY_KIND,
    ...SUBSCRIPTION_FEE_LEDGER_ENTRY_KIND,
    ...CONFIRMATION_CODE_LEDGER_ENTRY_KIND,
];
export const CHARGE_ATTEMPT_STATE = [
    "created",
    "sent",
    "succeeded",
    "declined",
    "requires_action",
    "requires_reauth",
    "ambiguous",
];
/**
 * THE BILLING PERIOD — plan-020 DOM (design §32.0.4, §32.13, §32.22.2; ADR 0069, proposed; glossary §6.35 "Billing
 * period"; owner answer OA-6; added 2026-10-16).
 *
 * The cadence of an offer's fee, as every contract names it (`billing_period`): the public pricing read, the offers
 * list, the quote detail, the subscription body and `recurring_charge_consent.billing_period`. SQL keeps
 * `commercial_revision.recurrence` (0086 admits `payg`, `monthly`, `annual`, `custom`); only the two recurring values
 * are billing periods. `annual` is modelled now and refused as unsellable (`offer_term_unsupported`) until its duties
 * are built (design §32.13).
 */
export const BILLING_PERIOD = ["monthly", "annual"];
const BILLING_PERIOD_SET = new Set(BILLING_PERIOD);
/**
 * Reads one billing period, throwing on anything else (design §32.20: "an unknown billing period … throws"). `payg`
 * and `custom` recurrences are not billing periods and throw too: a caller that holds one asked the wrong question.
 */
export function billingPeriodOf(value) {
    if (typeof value !== "string" || !BILLING_PERIOD_SET.has(value)) {
        throw new RangeError(`${JSON.stringify(value)} is not a billing period (one of ${BILLING_PERIOD.join(", ")})`);
    }
    return value;
}
/**
 * The subscription terms this release can sell (design §32.22.2, §32.13; plan-020 ED-4, OD-28): a `monthly` billing
 * period with no plan trial. `offerSellabilityOf` (SUB-KIND) refuses any other with `offer_term_unsupported`, so the
 * public read never lists one and no quote is made. A later ADR widens these lists together with the duties they owe
 * (the renewal reminder of a long cycle, the trial-ending mail and descriptor).
 */
export const SUPPORTED_SUBSCRIPTION_TERM = Object.freeze({
    billingPeriod: ["monthly"],
    trialDay: [null],
});
/**
 * Whether {@link SUPPORTED_SUBSCRIPTION_TERM} admits a revision's terms. Throws on a malformed term (an unknown billing
 * period, or a `trialDay` that is not `null` or a whole number 1–90 — 0158's CHECK) rather than calling it unsupported:
 * a malformed revision is a defect, an unsupported one is a refusal.
 */
export function isSupportedSubscriptionTerm(term) {
    const billingPeriod = billingPeriodOf(term.billingPeriod);
    const { trialDay } = term;
    if (trialDay !== null && !(Number.isInteger(trialDay) && trialDay >= 1 && trialDay <= 90)) {
        throw new RangeError(`${JSON.stringify(trialDay)} is not a plan trial (null, or a whole number of days 1–90)`);
    }
    const periodSupported = SUPPORTED_SUBSCRIPTION_TERM.billingPeriod.includes(billingPeriod);
    const trialSupported = SUPPORTED_SUBSCRIPTION_TERM.trialDay.includes(trialDay);
    return periodSupported && trialSupported;
}
//# sourceMappingURL=billing.js.map